import React, { useEffect, useState, useCallback, useRef } from "react";
import { exportOrder } from "@/lib/exportOrder";
import { useTableSort } from "@/lib/tableSort";

import { AddFirmDialog } from "@/components/AddFirmDialog";
import BulkStatusDialog from "@/components/BulkStatusDialog";
import TitleSicilFixDialog from "@/components/TitleSicilFixDialog";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Download, Upload, Filter, X, Loader2, Phone, Pencil, Trash2, ChevronLeft, ChevronRight, UserCheck, FileCheck, Users, ChevronDown, Star, FileText, BookUser } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStatusFilter } from "@/context/StatusFilterContext";
import {
  PREDICTION_OPTIONS, PREDICTION_FILTER_OPTIONS, PREDICTION_LABELS, PREDICTION_LABELS_NUM, STATUS_OPTIONS, STATUS_LABELS,
  VOTE_2022_LABELS, SECTORS, BRANCHES,
} from "@/lib/constants";
import { PredictionBadge, StatusBadge } from "@/components/badges";
import VoterEditDialog from "@/components/VoterEditDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const EXPORT_COL_OPTS = [
  ["sicil_no", "Sicil No"], ["istanbul_ticaret_sicil_no", "İTO Ticaret Sicil No"],
  ["company_name", "Unvan"], ["entity_class", "Şirket Çeşidi (Şahıs/Tüzel)"],
  ["firm_type", "Şirket Tipi"], ["contact_person", "Yetkili"], ["branch", "Şube"],
  ["city", "Şehir"], ["tsm_adi", "TSM Adı"], ["committee_no", "Komite No"], ["committee_name", "Komite"],
  ["status", "Durum"], ["current_vote_prediction_2026", "2026 Tahmin"],
  ["historical_vote_2022", "2022 Sonuç"], ["team_assignment_2026", "2026 Sorumlu"],
  ["team_assignment_2022", "2022 Sorumlu"], ["phone", "Telefon"],
  ["proxy_document", "Yetki Belgesi"], ["registration_year", "Kayıt Yılı"],
  ["historical_voter_type", "Seçmen Tipi"], ["sector_tags", "Sektörler"],
  ["address", "Adres"], ["partner_info", "Ortak"], ["last_note", "Son Yorum"],
  ["voting_person", "Oy Kullanacak Yetkili"], ["document_holder", "Belge Yeri"],
];
const EXPORT_DEFAULT_COLS = ["sicil_no", "company_name", "entity_class", "contact_person", "branch", "status", "current_vote_prediction_2026", "team_assignment_2026", "voting_person", "document_holder", "phone"];

const ALL = "__all__";
const PAGE_SIZE = 100;

export default function VoterList() {
  const { committee, setCommittee, user } = useAuth();
  const { status: globalStatus, setStatus: setGlobalStatus } = useStatusFilter();
  const [committees, setCommittees] = useState([]);
  useEffect(() => { api.get("/committees").then(({ data }) => setCommittees(data)).catch(() => {}); }, []);
  const committeeMap = React.useMemo(() => Object.fromEntries(committees.map((c) => [c.committee_no, c.name])), [committees]);
  const allView = committee === "all" || committee == null;
  const [voters, setVoters] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [bulkCaller, setBulkCaller] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);
  const [scopedTeam, setScopedTeam] = useState([]);
  const [sectors, setSectors] = useState(SECTORS);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 300); return () => clearTimeout(t); }, [search]);
  const [filters, setFilters] = useState({ status: globalStatus === "all" ? ALL : globalStatus, prediction: ALL, caller: ALL, branch: ALL, sector: ALL, vote_2022: ALL, proxy: ALL, ito: ALL, entity: ALL, holder: ALL, tsm: ALL });
  useEffect(() => {
    setFilters((f) => ({ ...f, status: globalStatus === "all" ? ALL : globalStatus }));
  }, [globalStatus]);
  const [editId, setEditId] = useState(null);
  const editScrollRef = useRef(0);
  useEffect(() => {
    if (editId) {
      editScrollRef.current = window.scrollY;
    } else if (editScrollRef.current > 0) {
      const y = editScrollRef.current;
      requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, y)));
    }
  }, [editId]);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();
  const canManage = user.role === "super_admin" || user.role === "committee_manager" || user.role === "branch_manager";
  const isGeneralManager = user.role === "super_admin";
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const isOwner = !!user.is_owner;
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [titleFixOpen, setTitleFixOpen] = useState(false);
  const [importFieldOpts, setImportFieldOpts] = useState([]);
  const [importFields, setImportFields] = useState([]);
  const [statusLockOn, setStatusLockOn] = useState(false);
  useEffect(() => {
    if (!canManage) return;
    api.get("/voters/import/fields")
      .then(({ data }) => { setImportFieldOpts(data.fields || []); setStatusLockOn(!!data.status_locked); })
      .catch(() => {});
  }, [canManage]);
  const canDeleteVoter = () => isOwner;
  const [deleteRequestTarget, setDeleteRequestTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [requesting, setRequesting] = useState(false);

  const submitDeleteRequest = async () => {
    setRequesting(true);
    try {
      await api.post(`/voters/${deleteRequestTarget.id}/delete-request`, { reason: deleteReason.trim() });
      toast.success("Silme talebi Genel Yönetici onayına gönderildi");
      setDeleteRequestTarget(null);
      setDeleteReason("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Talep oluşturulamadı");
    } finally {
      setRequesting(false);
    }
  };
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importFillOnly, setImportFillOnly] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [importReplace, setImportReplace] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportCols, setExportCols] = useState(EXPORT_DEFAULT_COLS);
  const [exporting, setExporting] = useState(false);
  const totalPages = 1;


  const [ownerMode, setOwnerMode] = useState(false);
  const [contactFilter, setContactFilter] = useState(false);
  const sentinelRef = React.useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || ownerMode || contactFilter) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading && voters.length > 0 && voters.length < total) {
        setPage((p) => p + 1);
      }
    }, { rootMargin: "400px" });
    io.observe(el);
    return () => io.disconnect();
  }, [loading, voters.length, total, ownerMode, contactFilter]);

  const [contactMatches, setContactMatches] = useState([]);
  useEffect(() => {
    try { const c = sessionStorage.getItem("imeak_contact_matches"); if (c) setContactMatches(JSON.parse(c).matches || []); } catch { /* ignore */ }
  }, []);
  const contactIds = React.useMemo(() => new Set(contactMatches.map((m) => m.id)), [contactMatches]);
  const contactScoped = React.useMemo(() => {
    let list = contactMatches;
    if (committee !== "all" && committee != null) list = list.filter((m) => m.committee_no === committee);
    const q = search.trim().toLocaleLowerCase("tr");
    if (q) list = list.filter((m) => (m.company_name || "").toLocaleLowerCase("tr").includes(q) || (m.contact_person || "").toLocaleLowerCase("tr").includes(q));
    return list;
  }, [contactMatches, committee, search]);
  const [owners, setOwners] = useState([]);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [expandedOwner, setExpandedOwner] = useState(null);
  const [ownerCompanies, setOwnerCompanies] = useState({});
  const [ownerCompaniesLoading, setOwnerCompaniesLoading] = useState(false);
  const [ownerSort, setOwnerSort] = useState("count");
  const [onlyCandidates, setOnlyCandidates] = useState(false);
  const ownerMeta = (o, companies) => {
    const list = companies || [];
    const assigned = o.assigned != null ? o.assigned : list.filter((c) => c.team_assignment_2026).length;
    const dist = {};
    const phones = [];
    list.forEach((c) => {
      const b = c.branch || "Bilinmiyor";
      dist[b] = (dist[b] || 0) + 1;
      if (c.phone && !phones.includes(c.phone)) phones.push(c.phone);
    });
    const branchList = Object.entries(dist).sort((a, b) => b[1] - a[1]);
    return { assigned, unassigned: o.count - assigned, isCandidate: o.count >= 2 && (o.assigned != null ? o.assigned : assigned) === 0, branchList, phones };
  };
  const [starring, setStarring] = useState(null);
  const toggleStar = async (o) => {
    setStarring(o.name);
    try {
      await api.post("/voters/owners/flag", { name: o.name, starred: !o.starred });
      setOwners((prev) => prev.map((x) => (x.name === o.name ? { ...x, starred: !o.starred } : x)));
    } catch (e) {
      toast.error("İşaretleme başarısız");
    } finally {
      setStarring(null);
    }
  };

  const loadOwners = useCallback(async () => {
    setOwnerLoading(true);
    setOwnerCompanies({});
    setExpandedOwner(null);
    const params = { sort: ownerSort };
    if (committee !== "all" && committee != null) params.committee_no = committee;
    try {
      const { data } = await api.get("/voters/owners", { params });
      setOwners(data);
    } finally {
      setOwnerLoading(false);
    }
  }, [committee, user.role, ownerSort]);

  useEffect(() => {
    if (!ownerMode || committee === null) return;
    const t = setTimeout(loadOwners, 250);
    return () => clearTimeout(t);
  }, [ownerMode, loadOwners, committee]);

  const OWNER_RENDER_LIMIT = 200;
  const displayOwners = React.useMemo(() => {
    let list = onlyCandidates ? owners.filter((o) => o.starred) : owners;
    const q = debouncedSearch.trim().toLocaleLowerCase("tr");
    if (q) list = list.filter((o) => (o.name || "").toLocaleLowerCase("tr").includes(q));
    return list;
  }, [owners, onlyCandidates, debouncedSearch]);
  const shownOwners = React.useMemo(() => displayOwners.slice(0, OWNER_RENDER_LIMIT), [displayOwners]);

  const [assignCaller, setAssignCaller] = useState("");
  const [assigning, setAssigning] = useState(false);
  const assignOwnerCompanies = async (o) => {
    if (!assignCaller) return;
    setAssigning(true);
    try {
      const ids = (ownerCompanies[o.name] || []).map((c) => c.id);
      const { data } = await api.post("/voters/bulk-assign", { ids, team_assignment_2026: assignCaller });
      toast.success(`${o.name} → ${data.updated} şirket ${assignCaller} kişisine atandı`);
      setAssignCaller("");
      setExpandedOwner(null);
      loadOwners();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAssigning(false);
    }
  };
  const openOwner = useCallback(async (o) => {
    setAssignCaller("");
    if (expandedOwner === o.name) { setExpandedOwner(null); return; }
    setExpandedOwner(o.name);
    if (!ownerCompanies[o.name]) {
      setOwnerCompaniesLoading(true);
      try {
        const params = { name: o.name };
        if (committee !== "all" && committee != null) params.committee_no = committee;
        const { data } = await api.get("/voters/owners/companies", { params });
        setOwnerCompanies((prev) => ({ ...prev, [o.name]: data }));
      } finally { setOwnerCompaniesLoading(false); }
    }
  }, [expandedOwner, ownerCompanies, committee]);

  useEffect(() => { api.get("/meta").then(({ data }) => { setTeam(data.team_members); setBranches(data.branches || []); if (data.sectors) setSectors(data.sectors); }); }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const b = searchParams.get("branch");
    const caller = searchParams.get("caller");
    if (b || caller) {
      setFilters((f) => ({ ...f, ...(b ? { branch: b } : {}), ...(caller ? { caller } : {}) }));
      if (b) searchParams.delete("branch");
      if (caller) searchParams.delete("caller");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sorumlu atama listesi seçili komiteye göre kısıtlanır.
  useEffect(() => {
    if (committee === "all" || committee == null) { setScopedTeam([]); return; }
    api.get("/responsibles", { params: { committee_no: committee } })
      .then(({ data }) => setScopedTeam(data.map((r) => r.name)))
      .catch(() => setScopedTeam([]));
  }, [committee]);

  const assignTeam = (committee !== "all" && committee != null) ? scopedTeam : team;

  const load = useCallback(async () => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE };
    if (committee !== "all" && committee != null) params.committee_no = committee;
    if (debouncedSearch) params.search = debouncedSearch;
    Object.entries(filters).forEach(([k, v]) => { if (v !== ALL) params[k] = v; });
    try {
      const { data } = await api.get("/voters/paged", { params });
      setVoters((prev) => (page === 1 ? data.items : [...prev, ...data.items]));
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [committee, user.role, debouncedSearch, filters, page]);

  useEffect(() => { setPage(1); setSelected([]); setVoters([]); }, [committee, debouncedSearch, filters]);

  useEffect(() => {
    if (committee === null) return;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, committee]);

  const branchOptions = React.useMemo(() => {
    const list = (branches.length ? branches : BRANCHES).filter(Boolean);
    const rest = list.filter((b) => b !== "Merkez").sort((a, b) => a.localeCompare(b, "tr"));
    return list.includes("Merkez") ? ["Merkez", ...rest] : rest;
  }, [branches]);

  const [tsmOptions, setTsmOptions] = useState([]);
  useEffect(() => {
    api.get("/voters/tsm-options").then(({ data }) => setTsmOptions(data)).catch(() => {});
  }, []);

  const setF = (k, v) => {
    setFilters((f) => ({ ...f, [k]: v }));
    if (k === "status") setGlobalStatus(v === ALL ? "all" : v);
  };
  const clearFilters = () => { setFilters({ status: globalStatus === "all" ? ALL : globalStatus, prediction: ALL, caller: ALL, branch: ALL, sector: ALL, vote_2022: ALL, proxy: ALL, ito: ALL, entity: ALL, holder: ALL, tsm: ALL }); setSearch(""); };
  const activeCount = Object.values(filters).filter((v) => v !== ALL).length + (search ? 1 : 0);

  const toggleOne = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const rows = contactFilter ? contactScoped : voters;
  const vlT = useTableSort(rows);
  const TH_CLS = "h-10 px-2 text-left align-middle font-medium text-muted-foreground";
  const pageIds = rows.map((v) => v.id);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const toggleAll = () => setSelected((s) => allSelected ? s.filter((id) => !pageIds.includes(id)) : [...new Set([...s, ...pageIds])]);

  const doBulkAssign = async () => {
    if (!bulkCaller || selected.length === 0) return;
    setBulkSaving(true);
    try {
      const { data } = await api.post("/voters/bulk-assign", { ids: selected, team_assignment_2026: bulkCaller });
      toast.success(`${data.updated} firma ${bulkCaller} kişisine atandı`);
      setSelected([]);
      setBulkCaller("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBulkSaving(false);
    }
  };

  const doBulkYetki = async (fmt) => {
    if (selected.length === 0) return;
    try {
      const res = await api.post("/voters/yetki-belgesi/bulk", { ids: selected, format: fmt }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Yetki_Belgeleri_${fmt}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${selected.length} firma için dilekçe (${fmt.toUpperCase()}) indirildi`);
    } catch {
      toast.error("Dilekçe oluşturulamadı");
    }
  };

  const doExport = async () => {
    if (ownerMode) {
      try {
        const params = { sort: ownerSort, only: 1, order: await exportOrder((owners || []).map((o) => o.name)) };
        if (committee !== "all" && committee != null) params.committee_no = committee;
        if (search) params.search = search;
        const res = await api.get("/voters/owners/export/xlsx", { params, responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url; a.download = "sahip_listesi.xlsx"; a.click();
        URL.revokeObjectURL(url);
        toast.success("Excel dışa aktarıldı");
      } catch (e) { toast.error("Dışa aktarma başarısız"); }
      return;
    }
    setExportOpen(true);
  };

  const runExport = async (format) => {
    if (exportCols.length === 0) { toast.error("En az bir sütun seçin"); return; }
    setExporting(true);
    try {
      const params = { format, columns: exportCols.join(","), order: await exportOrder(vlT.sorted.map((r) => r.id)) };
      if (committee !== "all" && committee != null) params.committee_no = committee;
      if (search) params.search = search;
      Object.entries(filters).forEach(([k, v]) => { if (v !== ALL) params[k] = v; });
      const res = await api.get("/voters/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `secmen_listesi_${new Date().toISOString().slice(0, 10)}.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} olarak dışa aktarıldı`);
      setExportOpen(false);
    } catch (e) {
      toast.error("Dışa aktarma başarısız");
    } finally {
      setExporting(false);
    }
  };

  const doTemplate = async () => {
    try {
      const res = await api.get("/voters/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "firma_sablonu.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Şablon indirilemedi");
    }
  };

  const runBuiltinPredictions = async () => {
    const key = `k${committee}`;
    if (!window.confirm(`Komite ${committee} 2026 tahminleri uygulamadaki Excel'e göre güncellenecek. Yetki belgesi, durum, şube, sorumlu ve notlara dokunulmaz. İşlem öncesi yedek alınır. Devam edilsin mi?`)) return;
    setImporting(true);
    setImportProgress(0);
    try {
      const { data: job } = await api.post(`/voters/predictions/builtin/${key}`);
      toast.info("İşlem başladı, arka planda sürüyor...");
      const done = await pollImportJob(job.job_id);
      if (done) {
        toast.success(`Tamamlandı: ${done.updated} firmanın tahmini güncellendi${done.unmatched ? `, ${done.unmatched} satır eşleşmedi` : ""}`);
        load();
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err));
    } finally {
      setImporting(false);
      setImportProgress(0);
    }
  };

  const runBuiltinImport = async () => {
    if (!window.confirm("Komite 25 kayıtları uygulamadaki Excel ile güvenli modda doldurulacak: şube güncellenir, yetkili/ortaklık/adres/telefon/sicil yalnızca boşsa doldurulur. Eğilim, yetki belgesi, durum, sorumlu ve notlara dokunulmaz. Devam edilsin mi?")) return;
    setImporting(true);
    setImportProgress(0);
    try {
      const { data: job } = await api.post("/voters/import/builtin/k25");
      toast.info("İşlem başladı, arka planda sürüyor...");
      const done = await pollImportJob(job.job_id);
      if (done) {
        toast.success(`Tamamlandı: ${done.updated} kayıt güncellendi`);
        load();
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err));
    } finally {
      setImporting(false);
      setImportProgress(0);
    }
  };

  const runFirmTypes = async () => {
    let pv;
    try {
      const { data } = await api.get("/voters/firm-types/preview");
      pv = data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err));
      return;
    }
    const lines = [
      "ÖNİZLEME (henüz hiçbir veri değişmedi):",
      `Toplam firma: ${pv.total}`,
      `Değişecek kayıt: ${pv.will_change}`,
      `Tüzel → Şahıs olacak: ${pv.to_sahis}`,
      `Şahıs → Tüzel olacak: ${pv.to_tuzel}`,
      `Sicili listede bulunamayıp unvandan şahıs sayılan: ${pv.by_name}`,
      "",
      pv.samples?.length ? `Örnekler:\n- ${pv.samples.join("\n- ")}` : "",
      "",
      "Yalnızca şirket çeşidi alanı yazılır; tahmin, yetki belgesi, durum, sorumlu ve notlara dokunulmaz. Uygulansın mı?",
    ].filter(Boolean).join("\n");
    if (!window.confirm(lines)) return;
    setImporting(true);
    setImportProgress(0);
    try {
      const { data: job } = await api.post("/voters/firm-types/apply");
      toast.info("Sınıflandırma başladı, arka planda sürüyor...");
      const done = await pollImportJob(job.job_id);
      if (done) {
        toast.success(`Tamamlandı: ${done.updated} firma güncellendi · Tüzel ${done.tuzel} · Şahıs ${done.sahis}`);
        load();
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err));
    } finally {
      setImporting(false);
      setImportProgress(0);
    }
  };

  const pollImportJob = async (jobId) => {
    for (let i = 0; i < 300; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      let st;
      try {
        ({ data: st } = await api.get(`/voters/import/jobs/${jobId}`));
      } catch {
        continue;
      }
      setImportProgress(st.progress || 0);
      if (st.status === "done") return st.result;
      if (st.status === "error") { toast.error(st.error || "İçe aktarım başarısız"); return null; }
    }
    toast.error("İçe aktarım zaman aşımına uğradı");
    return null;
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (committee === "all" || committee == null) {
      toast.error("İçe aktarmadan önce üstten bir komite seçin");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: job } = await api.post("/voters/import/xlsx", fd, { params: { committee_no: committee, dry_run: true, background: true }, headers: { "Content-Type": "multipart/form-data" } });
      const data = await pollImportJob(job.job_id);
      if (!data) return;
      setImportFile(file);
      setImportFields([]);
      setImportPreview(data);
      setImportOpen(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const confirmImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data: job } = await api.post("/voters/import/xlsx", fd, {
        params: { committee_no: committee, replace: importReplace, background: true, fill_only: importFillOnly,
          fields: importFields.join(",") },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportOpen(false);
      toast.info("İçe aktarım başladı, arka planda sürüyor...");
      const done = await pollImportJob(job.job_id);
      if (done) {
        toast.success(`İçe aktarıldı: ${done.created} yeni, ${done.updated} güncellendi${done.skipped_count ? `, ${done.skipped_count} satır atlandı` : ""}`);
        load();
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err));
    } finally {
      setImporting(false);
      setImportProgress(0);
      setImportOpen(false);
      setImportFile(null);
      setImportPreview(null);
      setImportReplace(false);
    }
  };

  const FilterSelect = ({ k, placeholder, options, labels }) => (
    <Select value={filters[k]} onValueChange={(v) => setF(k, v)}>
      <SelectTrigger className="h-9 w-full" data-testid={`filter-${k}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{placeholder}: Tümü</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{labels ? labels[o] : o}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-4" data-testid="voter-list-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground">Seçmen Listesi</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm text-muted-foreground">{contactFilter ? `Rehberimde ${contactScoped.length} firma` : `Toplam ${total} kayıt`}{!contactFilter && voters.length > 0 && voters.length < total ? ` · ${voters.length} yüklendi` : ""}</p>
            {user.role === "branch_manager" && (
              <Badge variant="secondary" className="gap-1 bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-300 hover:bg-cyan-100" data-testid="branch-scope-badge">
                Şube: {(user.branch_names || []).join(", ") || "atanmadı"} · yalnızca bu şubenin firmaları
              </Badge>
            )}
            {canManage && selected.length > 0 && (
              <Badge className="bg-cyan-500 hover:bg-cyan-500 text-white gap-1" data-testid="selection-badge">
                <UserCheck className="h-3 w-3" />{selected.length} firma seçili (tüm sayfalar)
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={ownerMode ? "default" : "outline"} size="sm" onClick={() => { setOwnerMode((m) => !m); setExpandedOwner(null); setContactFilter(false); }} className={ownerMode ? "bg-[#0F2C59] hover:bg-[#0F2C59]/90" : ""} data-testid="owner-mode-toggle">
            <Users className="h-4 w-4 mr-2" />{ownerMode ? "Firma Bazlı" : "Sahip Bazlı"}
          </Button>
          {contactMatches.length > 0 && (
            <Button variant={contactFilter ? "default" : "outline"} size="sm" onClick={() => { setContactFilter((v) => !v); setOwnerMode(false); setSelected([]); }} className={contactFilter ? "bg-emerald-600 hover:bg-emerald-600/90" : ""} data-testid="contact-filter-toggle">
              <BookUser className="h-4 w-4 mr-2" />Rehberimdekiler ({contactScoped.length})
            </Button>
          )}
          {canManage && (
            <>
              {!ownerMode && isGeneralManager && (<>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={doImport} data-testid="import-file-input" />
              <Button variant="outline" size="sm" onClick={doTemplate} data-testid="template-button">
                <Download className="h-4 w-4 mr-2" />Şablon İndir
              </Button>
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="import-button">
                {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                {importing ? (importProgress ? `İçe aktarılıyor… %${importProgress}` : "İçe aktarılıyor…") : "Excel İçe Aktar"}
              </Button>
              {String(committee) === "25" && (
                <Button variant="outline" size="sm" onClick={runBuiltinImport} disabled={importing} data-testid="builtin-import-button">
                  {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  {importing ? (importProgress ? `Dolduruluyor… %${importProgress}` : "Dolduruluyor…") : "K25 Excel'den Güvenli Doldur"}
                </Button>
              )}
              {["5", "7", "37"].includes(String(committee)) && (
                <Button variant="outline" size="sm" onClick={runBuiltinPredictions} disabled={importing} data-testid="builtin-predictions-button">
                  {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  {importing ? (importProgress ? `Uygulanıyor… %${importProgress}` : "Uygulanıyor…") : `K${committee} Excel'den 2026 Tahminlerini Uygula`}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={runFirmTypes} disabled={importing} data-testid="firm-types-button">
                {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                {importing ? (importProgress ? `Sınıflandırılıyor… %${importProgress}` : "Sınıflandırılıyor…") : "Firma Tiplerini Excel'den Uygula"}
              </Button>
              </>)}
              <Button variant="outline" size="sm" onClick={doExport} data-testid="export-button">
                <Download className="h-4 w-4 mr-2" />Dışarı Aktar
              </Button>
            </>
          )}
          {isGeneralManager && (
            <>
              <Button variant="outline" size="sm" onClick={() => setBulkStatusOpen(true)} data-testid="bulk-status-button" title="ASKI/FAAL kilidi Ayarlar'dan yönetilir">
                <FileCheck className="h-4 w-4 mr-2" />Sicil No ile Toplu Durum
              </Button>
              <BulkStatusDialog open={bulkStatusOpen} onOpenChange={setBulkStatusOpen} onDone={load} />
              <Button variant="outline" size="sm" onClick={() => setTitleFixOpen(true)} data-testid="title-fix-button">
                <Upload className="h-4 w-4 mr-2" />Unvan & Sicil Düzelt (Excel)
              </Button>
              <TitleSicilFixDialog open={titleFixOpen} onOpenChange={setTitleFixOpen} onDone={load} />
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-600/90" onClick={() => setAddOpen(true)} data-testid="add-firm-button">
                <Users className="h-4 w-4 mr-2" />Yeni Firma
              </Button>
              <AddFirmDialog open={addOpen} onOpenChange={setAddOpen} committees={committees} branches={branches} onCreated={() => { setAddOpen(false); load(); }} />
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 border border-border">
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={ownerMode ? "Sahip / ortak adı ara..." : "Firma adı, sicil no veya kayıt yılı ara..."} className="pl-9" data-testid="search-input" />
          </div>
          {ownerMode && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="owner-sort">
              <span className="text-xs text-muted-foreground">Sırala:</span>
              {[
                { v: "count", l: "Şirket Sayısı" },
                { v: "kesin", l: "Kesin Oy Gücü" },
                { v: "faal", l: "FAAL Oy Gücü" },
              ].map((s) => (
                <button key={s.v} type="button" onClick={() => setOwnerSort(s.v)} data-testid={`owner-sort-${s.v}`}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${ownerSort === s.v ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  {s.l}
                </button>
              ))}
              <button type="button" onClick={() => setOnlyCandidates((v) => !v)} data-testid="owner-only-candidates"
                className={`ml-1 px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${onlyCandidates ? "bg-amber-500 text-white border-amber-500" : "border-border text-muted-foreground hover:text-foreground"}`}>
                <Star className="h-3 w-3 inline mr-1 -mt-0.5" />Öncelikli Adaylar
              </button>
            </div>
          )}
          {!ownerMode && (<>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-8 gap-2">
            {isGeneralManager
              ? <FilterSelect k="status" placeholder="Durum" options={STATUS_OPTIONS} labels={STATUS_LABELS} />
              : <div className="h-9 px-3 inline-flex items-center rounded-md border border-border text-sm font-medium text-muted-foreground" data-testid="voter-status-fixed">Durum: FAAL</div>}
            <FilterSelect k="prediction" placeholder="2026 Tahmin" options={PREDICTION_FILTER_OPTIONS} labels={PREDICTION_LABELS_NUM} />
            {canManage && <FilterSelect k="caller" placeholder="Sorumlu" options={assignTeam} />}
            <FilterSelect k="branch" placeholder="Şube" options={branchOptions} />
            {committee !== "all" && committee != null && <FilterSelect k="sector" placeholder="Sektör" options={sectors} />}
            <FilterSelect k="proxy" placeholder="Yetki Belgesi" options={["yes", "no"]} labels={{ yes: "Belgeli", no: "Belgesiz" }} />
            <FilterSelect k="ito" placeholder="İТО Sicil No" options={["yes", "no"]} labels={{ yes: "İТО No Var", no: "İТО No Eksik" }} />
            <FilterSelect k="tsm" placeholder="Sicil İli" options={tsmOptions} />
            <FilterSelect k="entity" placeholder="Firma Tipi" options={["tuzel", "sahis"]} labels={{ tuzel: "Tüzel Şirket", sahis: "Şahıs Şirketi" }} />
            <FilterSelect k="holder" placeholder="Belge Yeri" options={["merkez", "sube", "kendi", "kisi", "diger", "bos"]}
              labels={{ merkez: "Seçim Ofisinde", sube: "Şubede", kendi: "Kendi Aldı", kisi: "Kişide", diger: "Diğer", bos: "Girilmemiş" }} />
          </div>
          {activeCount > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <Filter className="h-4 w-4 text-cyan-600" />
              <span className="text-muted-foreground">{activeCount} filtre aktif</span>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={clearFilters} data-testid="clear-filters"><X className="h-3 w-3 mr-1" />Temizle</Button>
            </div>
          )}
          </>)}
        </div>
      </Card>

      {!ownerMode && canManage && selected.length > 0 && (
        <Card className="p-3 border border-cyan-500/40 bg-cyan-50 dark:bg-cyan-950/30" data-testid="bulk-bar">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-foreground flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-cyan-600" />{selected.length} firma seçildi
              {selected.length - pageIds.filter((id) => selected.includes(id)).length > 0 && (
                <span className="text-xs text-muted-foreground">({selected.length - pageIds.filter((id) => selected.includes(id)).length} tanesi henüz yüklenmemiş kayıtlarda)</span>
              )}
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <Select value={bulkCaller} onValueChange={setBulkCaller}>
                <SelectTrigger className="h-9 w-[180px]" data-testid="bulk-caller-select"><SelectValue placeholder="Sorumlu seç" /></SelectTrigger>
                <SelectContent>{assignTeam.length ? assignTeam.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>) : <div className="px-2 py-1.5 text-xs text-muted-foreground">Bu komiteye atanmış sorumlu yok</div>}</SelectContent>
              </Select>
              <Button size="sm" className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" disabled={!bulkCaller || bulkSaving} onClick={doBulkAssign} data-testid="bulk-assign-button">
                {bulkSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Toplu Ata"}
              </Button>
              {isOwner && (
                <Button size="sm" variant="destructive" onClick={() => setBulkDeleteOpen(true)} data-testid="bulk-delete-button">
                  <Trash2 className="h-4 w-4 mr-1" />Toplu Sil
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => doBulkYetki("docx")} data-testid="bulk-yetki-docx">
                <FileText className="h-4 w-4 mr-1" />Dilekçe (Word)
              </Button>
              <Button size="sm" variant="outline" onClick={() => doBulkYetki("pdf")} data-testid="bulk-yetki-pdf">
                <FileText className="h-4 w-4 mr-1" />Dilekçe (PDF)
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected([])} data-testid="bulk-clear">Vazgeç</Button>
            </div>
          </div>
        </Card>
      )}

      {ownerMode && (
        ownerLoading ? (
          <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}</div>
        ) : (
          <div className="space-y-2" data-testid="owner-list">
            {shownOwners.map((o, idx) => (
              <Card key={o.name} className="border border-border overflow-hidden" data-testid={`owner-row-${idx}`}>
                <button type="button" className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/40" onClick={() => openOwner(o)} data-testid={`owner-toggle-${idx}`}>
                  <span className="text-sm font-bold text-muted-foreground w-7 shrink-0">{idx + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{o.name}</p>
                    <p className="text-xs text-muted-foreground">{o.count} şirket &bull; oy hakkı</p>
                  </div>
                  <div className="hidden sm:flex items-center gap-1.5 shrink-0">
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" title="FAAL (oy kullanabilir) şirket sayısı">{o.faal} FAAL</span>
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300" title="Kesin/güvence altındaki şirket sayısı">{o.kesin} Kesin</span>
                  </div>
                  <Badge className="bg-[#0F2C59] hover:bg-[#0F2C59] text-white shrink-0">{o.count}</Badge>
                  {o.starred ? (
                    <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300 shrink-0" data-testid={`owner-candidate-badge-${idx}`}>
                      <Star className="h-3 w-3 fill-amber-400 text-amber-500" />Öncelikli Aday
                    </span>
                  ) : ownerMeta(o).isCandidate ? (
                    <span className="hidden md:inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0" title="Yüksek oy gücü, henüz sorumlusu yok — öneri" data-testid={`owner-suggest-badge-${idx}`}>
                      Öneri
                    </span>
                  ) : null}
                  {canManage && (
                    <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); if (starring !== o.name) toggleStar(o); }} data-testid={`owner-star-${idx}`}
                      className="shrink-0 p-1 rounded hover:bg-muted" title={o.starred ? "Öncelikli adaylıktan çıkar" : "Öncelikli aday olarak işaretle"}>
                      <Star className={`h-4 w-4 ${o.starred ? "fill-amber-400 text-amber-500" : "text-muted-foreground"}`} />
                    </span>
                  )}
                  <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${expandedOwner === o.name ? "rotate-180" : ""}`} />
                </button>
                {expandedOwner === o.name && (
                  <div className="border-t border-border" data-testid={`owner-companies-${idx}`}>
                    {(!ownerCompanies[o.name] && ownerCompaniesLoading) ? (
                      <div className="p-6 flex justify-center" data-testid={`owner-companies-loading-${idx}`}><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                    ) : (() => {
                      const comps = ownerCompanies[o.name] || [];
                      const m = ownerMeta(o, comps);
                      return (
                      <>
                      <div className="p-4 bg-muted/30 border-b border-border space-y-3" data-testid={`owner-detail-${idx}`}>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div><p className="text-lg font-bold text-[#0F2C59] dark:text-cyan-300">{o.count}</p><p className="text-[11px] text-muted-foreground">Toplam Şirket</p></div>
                          <div><p className="text-lg font-bold text-emerald-600">{o.faal}</p><p className="text-[11px] text-muted-foreground">FAAL Oy Gücü</p></div>
                          <div><p className="text-lg font-bold text-blue-600">{o.kesin}</p><p className="text-[11px] text-muted-foreground">Kesin Oy</p></div>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">Şube Dağılımı</p>
                          <div className="flex flex-wrap gap-1.5">
                            {m.branchList.map(([b, n]) => <span key={b} className="text-[11px] px-2 py-0.5 rounded-full bg-card border border-border">{b} &middot; {n}</span>)}
                          </div>
                        </div>
                        {isOwner && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">İletişim ({m.phones.length})</p>
                          {m.phones.length ? (
                            <div className="flex flex-wrap gap-x-4 gap-y-1">{m.phones.map((p) => <span key={p} className="text-xs inline-flex items-center gap-1"><Phone className="h-3 w-3 text-muted-foreground" />{p}</span>)}</div>
                          ) : <p className="text-xs text-muted-foreground">Telefon bilgisi yok</p>}
                        </div>
                        )}
                        <p className="text-xs">
                          {m.assigned === o.count ? <span className="text-emerald-600">✓ Tüm şirketlerin sorumlusu var</span> : <span className="text-amber-600">⚠ {m.unassigned} şirketin henüz sorumlusu yok</span>}
                        </p>
                      </div>
                      {canManage && (
                        <div className="flex items-center gap-2 p-3 bg-muted/40 flex-wrap" data-testid={`owner-assign-bar-${idx}`}>
                          <UserCheck className="h-4 w-4 text-cyan-600 shrink-0" />
                          <span className="text-xs text-muted-foreground">Tüm {o.count} şirketi şuna ata:</span>
                          <Select value={assignCaller} onValueChange={setAssignCaller}>
                            <SelectTrigger className="h-8 w-[160px]" data-testid={`owner-assign-select-${idx}`}><SelectValue placeholder="Sorumlu seç" /></SelectTrigger>
                            <SelectContent>{assignTeam.map((mm) => <SelectItem key={mm} value={mm}>{mm}</SelectItem>)}</SelectContent>
                          </Select>
                          <Button size="sm" className="h-8 bg-[#0F2C59] hover:bg-[#0F2C59]/90" disabled={!assignCaller || assigning} onClick={() => assignOwnerCompanies(o)} data-testid={`owner-assign-btn-${idx}`}>
                            {assigning ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ata"}
                          </Button>
                        </div>
                      )}
                      <div className="divide-y divide-border">
                      {comps.map((v) => (
                        <div key={v.id} className="flex items-center gap-2 p-3 hover:bg-muted/30 cursor-pointer" onClick={() => setEditId(v.id)} data-testid={`owner-company-${v.id}`}>
                          <span className="inline-flex items-center gap-1.5 min-w-0 flex-1">
                            {v.proxy_document && <FileCheck className="h-4 w-4 text-emerald-600 shrink-0" />}
                            <span className="min-w-0">
                              <span className="block truncate text-sm">{v.company_name}</span>
                              {v.committee_no != null && (
                                <span className="block text-[11px] text-muted-foreground truncate" data-testid={`owner-company-committee-${v.id}`}>
                                  #{v.committee_no} {committeeMap[v.committee_no] || ""}
                                </span>
                              )}
                            </span>
                            {v.team_assignment_2026 && <span className="text-[11px] text-muted-foreground shrink-0">· {v.team_assignment_2026}</span>}
                          </span>
                          <StatusBadge value={v.status} />
                          <PredictionBadge value={v.current_vote_prediction_2026} />
                          <Pencil className="h-4 w-4 text-muted-foreground shrink-0" />
                        </div>
                      ))}
                      </div>
                      </>
                      );
                    })()}
                  </div>
                )}
              </Card>
            ))}
            {displayOwners.length === 0 && <p className="text-center py-12 text-muted-foreground">Kayıt bulunamadı</p>}
            {displayOwners.length > OWNER_RENDER_LIMIT && (
              <p className="text-center py-4 text-xs text-muted-foreground" data-testid="owner-limit-note">
                {displayOwners.length} sonuçtan ilk {OWNER_RENDER_LIMIT} tanesi gösteriliyor. Aramayı daraltarak istediğinizi bulun.
              </p>
            )}
          </div>
        )
      )}

      {!ownerMode && (loading && !contactFilter ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
      ) : (
        <>
          {/* Desktop table */}
          <Card className="border border-border hidden md:block overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    {canManage && (
                      <TableHead className="w-10">
                        <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-checkbox" aria-label="Tümünü seç" />
                      </TableHead>
                    )}
                    <vlT.Th sortKey="vl-firma" get={(r) => r.company_name} text className={TH_CLS}>Firma (Unvan)</vlT.Th>
                    {allView && <vlT.Th sortKey="vl-komite" get={(r) => r.committee_no} className={TH_CLS}>Komite</vlT.Th>}
                    <vlT.Th sortKey="vl-sube" get={(r) => r.branch} text className={TH_CLS}>Şube</vlT.Th>
                    <vlT.Th sortKey="vl-ito" get={(r) => r.istanbul_ticaret_sicil_no} text className={TH_CLS}>İТО No</vlT.Th>
                    <vlT.Th sortKey="vl-durum" get={(r) => r.status} text className={TH_CLS}>Durum</vlT.Th>
                    <vlT.Th sortKey="vl-2026" get={(r) => r.current_vote_prediction_2026 || ""} text className={TH_CLS}>2026 Tahmin</vlT.Th>
                    <vlT.Th sortKey="vl-sorumlu" get={(r) => r.team_assignment_2026} text className={TH_CLS}>Sorumlu</vlT.Th>
                    <vlT.Th sortKey="vl-oyveren" get={(r) => r.voting_person || ""} text className={TH_CLS}>Oy Veren</vlT.Th>
                    <vlT.Th sortKey="vl-belgekimde" get={(r) => r.document_holder || ""} text className={TH_CLS}>Belge Yeri</vlT.Th>
                    <TableHead className="text-right">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vlT.sorted.map((v) => (
                    <TableRow key={v.id} className="cursor-pointer hover:bg-muted/40" data-testid={`voter-row-${v.id}`}>
                      {canManage && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={selected.includes(v.id)} onCheckedChange={() => toggleOne(v.id)} data-testid={`select-${v.id}`} aria-label="Firma seç" />
                        </TableCell>
                      )}
                      <TableCell className="font-medium max-w-[260px]" onClick={() => setEditId(v.id)}>
                        <span className="inline-flex items-center gap-1.5 max-w-full">
                          {v.proxy_document && <FileCheck className="h-4 w-4 text-emerald-600 shrink-0" data-testid={`proxy-icon-${v.id}`} title="Yetki belgesi alındı" />}
                          <span className="truncate min-w-0">{v.company_name}</span>
                          {v.entity_class === "sahis" && (
                            <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300 shrink-0" data-testid={`sahis-badge-${v.id}`} title="Şahıs şirketi — yetki belgesi gerekmez, kimlikle oy kullanır">
                              Şahıs
                            </span>
                          )}
                          {contactIds.has(v.id) && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 shrink-0" data-testid={`contact-badge-${v.id}`} title="Telefon rehberinizde kayıtlı">
                              <Phone className="h-2.5 w-2.5" />Rehberde
                            </span>
                          )}
                        </span>
                        {!allView && v.committee_no != null && (
                          <span className="block text-[11px] font-normal text-muted-foreground truncate" data-testid={`voter-committee-${v.id}`}>
                            #{v.committee_no} {committeeMap[v.committee_no] || ""}
                          </span>
                        )}
                        {v.contact_person && <span className="block text-[11px] font-normal text-muted-foreground truncate" data-testid={`voter-contact-${v.id}`}>{v.contact_person}</span>}
                      </TableCell>
                      {allView && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <button type="button" onClick={(e) => { e.stopPropagation(); setCommittee(v.committee_no); }}
                            className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#0F2C59]/10 text-[#0F2C59] dark:bg-sky-500/15 dark:text-sky-300 whitespace-nowrap hover:bg-[#0F2C59]/20 transition-colors cursor-pointer"
                            data-testid={`committee-badge-${v.id}`} title={`${committeeMap[v.committee_no] || ""} — bu komiteye geç`}>
                            #{v.committee_no}{committeeMap[v.committee_no] ? ` ${committeeMap[v.committee_no].slice(0, 28)}${committeeMap[v.committee_no].length > 28 ? "…" : ""}` : ""}
                          </button>
                        </TableCell>
                      )}
                      <TableCell className="text-muted-foreground" onClick={() => setEditId(v.id)}>{v.branch}</TableCell>
                      <TableCell onClick={() => setEditId(v.id)}>
                        {v.istanbul_ticaret_sicil_no ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" data-testid={`ito-badge-${v.id}`} title={`İТО Ticaret Sicil No: ${v.istanbul_ticaret_sicil_no}`}>
                            <FileCheck className="h-3 w-3" />{v.istanbul_ticaret_sicil_no}
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300" data-testid={`ito-badge-${v.id}`} title="İТО Ticaret Sicil No eksik">
                            Eksik
                          </span>
                        )}
                      </TableCell>
                      <TableCell onClick={() => setEditId(v.id)}><StatusBadge value={v.status} /></TableCell>
                      <TableCell onClick={() => setEditId(v.id)}><PredictionBadge value={v.current_vote_prediction_2026} /></TableCell>
                      <TableCell className="text-muted-foreground" onClick={() => setEditId(v.id)}>{v.team_assignment_2026 || "-"}</TableCell>
                      <TableCell className="text-muted-foreground max-w-[170px]" onClick={() => setEditId(v.id)}>
                        <span className="block truncate" data-testid={`voting-person-${v.id}`} title={v.voting_person || ""}>{v.voting_person || "—"}</span>
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-[170px]" onClick={() => setEditId(v.id)}>
                        {v.merkez_pending ? (
                          <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300" data-testid={`holder-pending-${v.id}`}>
                            Seçim Ofisi onayı bekliyor
                          </span>
                        ) : (
                          <span className="block truncate" data-testid={`document-holder-${v.id}`} title={v.document_holder || ""}>{v.document_holder || "—"}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); setEditId(v.id); }} data-testid={`edit-voter-${v.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {canDeleteVoter(v) ? (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(v); }} data-testid={`delete-voter-${v.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : user.role === "committee_manager" ? (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600" title="Silme talebi oluştur"
                              onClick={(e) => { e.stopPropagation(); setDeleteRequestTarget(v); setDeleteReason(""); }} data-testid={`delete-request-${v.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={(canManage ? 10 : 9) + (allView ? 1 : 0)} className="text-center py-12 text-muted-foreground">Kayıt bulunamadı</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {rows.map((v) => (
              <Card key={v.id} className="p-4 border border-border" onClick={() => setEditId(v.id)} data-testid={`voter-card-${v.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-sm flex items-center gap-1.5 min-w-0">
                      {v.proxy_document && <FileCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0" />}
                      <span className="truncate min-w-0">{v.company_name}</span>
                      {v.entity_class === "sahis" && (
                        <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300 shrink-0" data-testid={`sahis-badge-m-${v.id}`}>Şahıs</span>
                      )}
                    </p>
                    {contactIds.has(v.id) && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 mt-1" data-testid={`contact-badge-m-${v.id}`}>
                        <Phone className="h-2.5 w-2.5" />Rehberde
                      </span>
                    )}
                    {v.committee_no != null && (
                      <p className="text-[11px] text-muted-foreground truncate" data-testid={`voter-card-committee-${v.id}`}>
                        #{v.committee_no} {committeeMap[v.committee_no] || ""}
                      </p>
                    )}
                    {v.contact_person && <p className="text-[11px] text-muted-foreground truncate" data-testid={`voter-card-contact-${v.id}`}>{v.contact_person}</p>}
                    <p className="text-xs text-muted-foreground">{v.branch} &bull; {v.registration_year} &bull; {v.team_assignment_2026 || "-"}</p>
                    <p className="text-[11px] mt-0.5">
                      {v.istanbul_ticaret_sicil_no
                        ? <span className="text-emerald-600 dark:text-emerald-400">İТО No: {v.istanbul_ticaret_sicil_no}</span>
                        : <span className="text-amber-600 dark:text-amber-400">İТО No eksik</span>}
                    </p>
                  </div>
                  <StatusBadge value={v.status} />
                </div>
                <div className="flex items-center justify-between mt-3">
                  <PredictionBadge value={v.current_vote_prediction_2026} />
                  <div className="flex items-center gap-2">
                    {canDeleteVoter(v) ? (
                      <Button size="sm" variant="ghost" className="h-8 text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(v); }} data-testid={`delete-voter-m-${v.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    ) : user.role === "committee_manager" ? (
                      <Button size="sm" variant="ghost" className="h-8 text-amber-600" onClick={(e) => { e.stopPropagation(); setDeleteRequestTarget(v); setDeleteReason(""); }} data-testid={`delete-request-m-${v.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    ) : null}
                    <Button size="sm" variant="outline" className="h-8"><Pencil className="h-3.5 w-3.5 mr-1" />Düzenle</Button>
                  </div>
                </div>
              </Card>
            ))}
            {rows.length === 0 && <p className="text-center py-12 text-muted-foreground">Kayıt bulunamadı</p>}
          </div>
        </>
      ))}

      {!ownerMode && !contactFilter && (
        <div ref={sentinelRef} className="py-4 text-center" data-testid="infinite-scroll-sentinel">
          {loading && voters.length > 0 && (
            <span className="inline-flex items-center gap-2 text-sm text-muted-foreground" data-testid="infinite-loading">
              <Loader2 className="h-4 w-4 animate-spin" />Yükleniyor...
            </span>
          )}
          {!loading && total > 0 && (
            <span className="text-xs text-muted-foreground" data-testid="infinite-count">
              {voters.length >= total
                ? `Tüm kayıtlar yüklendi · ${total} kayıt`
                : `${voters.length} / ${total} kayıt · aşağı kaydırdıkça yüklenir`}
            </span>
          )}
        </div>
      )}

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-lg" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle>Dışa Aktar</DialogTitle>
            <DialogDescription>
              Yalnızca <b>şu an filtrelenmiş liste</b> aktarılır. Hangi bilgilerin yer alacağını seçin, ardından biçim seçin.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{exportCols.length} sütun seçili</span>
            <div className="flex gap-3">
              <button type="button" className="text-[#0F2C59] hover:underline" onClick={() => setExportCols(EXPORT_COL_OPTS.map((c) => c[0]))} data-testid="export-select-all">Tümünü seç</button>
              <button type="button" className="text-[#0F2C59] hover:underline" onClick={() => setExportCols(EXPORT_DEFAULT_COLS)} data-testid="export-reset">Varsayılan</button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 max-h-64 overflow-y-auto rounded-lg border border-border p-3">
            {EXPORT_COL_OPTS.map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`export-col-${k}`}>
                <Checkbox
                  checked={exportCols.includes(k)}
                  onCheckedChange={(ck) => setExportCols((prev) => ck ? [...prev, k] : prev.filter((x) => x !== k))}
                />
                {label}
              </label>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => runExport("xlsx")} disabled={exporting || exportCols.length === 0} data-testid="export-xlsx-btn">
              <Download className="h-4 w-4 mr-2" />Excel (.xlsx)
            </Button>
            <Button onClick={() => runExport("pdf")} disabled={exporting || exportCols.length === 0} className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="export-pdf-btn">
              <FileText className="h-4 w-4 mr-2" />PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VoterEditDialog voterId={editId} open={!!editId} onClose={() => setEditId(null)} onSaved={() => load()} team={team} sectors={sectors} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-voter-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Firma silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>{deleteTarget?.company_name} kalıcı olarak silinecek. Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-voter"
              onClick={async () => {
                const vid = deleteTarget.id;
                const vname = deleteTarget.company_name;
                try {
                  await api.delete(`/voters/${vid}`);
                  setDeleteTarget(null);
                  load();
                  toast.success(`${vname} silindi`, {
                    duration: 7000,
                    action: {
                      label: "Geri Al",
                      onClick: async () => {
                        try { await api.post(`/voters/${vid}/restore`); toast.success("Firma geri alındı"); load(); }
                        catch { toast.error("Geri alma başarısız"); }
                      },
                    },
                  });
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail));
                }
              }}>Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteRequestTarget} onOpenChange={(o) => !o && setDeleteRequestTarget(null)}>
        <AlertDialogContent data-testid="delete-request-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Silme talebi oluştur</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteRequestTarget?.company_name} için silme talebi Genel Yönetici onayına gönderilecek. Gerekçe zorunludur.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)}
            placeholder="Gerekçe: ör. firma kapandı / mükerrer kayıt" data-testid="delete-request-reason" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-request-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction disabled={requesting || deleteReason.trim().length < 5}
              onClick={submitDeleteRequest} data-testid="delete-request-submit">Talebi Gönder</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      <AlertDialog open={bulkDeleteOpen} onOpenChange={(o) => !o && setBulkDeleteOpen(false)}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{selected.length} firma silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Seçili {selected.length} firma silinecek. Silme sonrası kısa süreliğine "Geri Al" ile toplu geri getirebilirsiniz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-bulk-delete"
              disabled={bulkDeleting}
              onClick={async () => {
                setBulkDeleting(true);
                try {
                  const { data } = await api.post("/voters/bulk-delete", { ids: selected });
                  const removed = data.ids || [];
                  setBulkDeleteOpen(false);
                  setSelected([]);
                  load();
                  toast.success(`${data.deleted} firma silindi`, {
                    duration: 8000,
                    action: {
                      label: "Geri Al",
                      onClick: async () => {
                        try { const { data: r } = await api.post("/voters/bulk-restore", { ids: removed }); toast.success(`${r.restored} firma geri alındı`); load(); }
                        catch { toast.error("Geri alma başarısız"); }
                      },
                    },
                  });
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail));
                } finally {
                  setBulkDeleting(false);
                }
              }}>{bulkDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sil"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={importOpen} onOpenChange={(o) => { if (!o) { setImportOpen(false); setImportFile(null); setImportPreview(null); setImportReplace(false); } }}>
        <AlertDialogContent data-testid="import-preview-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>İçe Aktarım Önizleme</AlertDialogTitle>
            <AlertDialogDescription>
              {importPreview && importPreview.total > 0
                ? `${importPreview.total} satır işlenecek: ${importPreview.created} yeni firma eklenecek, ${importPreview.updated} mevcut firma güncellenecek.`
                : "Dosyada geçerli firma satırı bulunamadı. Lütfen şablona uygun bir dosya yükleyin."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {importPreview && importPreview.skipped_count > 0 && (
            <div className="rounded-md border border-amber-300 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 p-3" data-testid="import-skipped-report">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-1.5">
                {importPreview.skipped_count} satır atlanacak (hatalı):
              </p>
              <ul className="max-h-40 overflow-y-auto space-y-1 text-xs text-amber-700 dark:text-amber-400">
                {importPreview.skipped.map((s, i) => (
                  <li key={i} data-testid={`import-skipped-row-${i}`}>
                    <b>Satır {s.row}</b>{s.value ? ` (${s.value})` : ""}: {s.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {importPreview && importPreview.total > 0 && (
            <div className="rounded-md border border-border p-3" data-testid="import-fields-picker">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">Güncellenecek sütunları seçin ({importFields.length} seçili)</p>
                <div className="flex gap-1.5">
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-xs"
                    onClick={() => { setImportFillOnly(false); setImportFields(importFieldOpts.map((f) => f.key)); }} data-testid="import-fields-all">Tümünü seç</Button>
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-xs"
                    onClick={() => setImportFields([])} data-testid="import-fields-none">Temizle</Button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-52 overflow-y-auto">
                {importFieldOpts.map((f) => (
                  <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/40 rounded px-1.5 py-1">
                    <input type="checkbox" className="h-4 w-4 accent-[#0F2C59]"
                      checked={importFields.includes(f.key)}
                      onChange={(e) => { setImportFillOnly(false); setImportFields((prev) => e.target.checked ? [...prev, f.key] : prev.filter((x) => x !== f.key)); }}
                      data-testid={`import-field-${f.key}`} />
                    <span>{f.label}</span>
                    {f.key === "status" && statusLockOn && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">kilitli</span>}
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Sadece tik attığınız sütunlar mevcut firmalarda güncellenir; tiklemedikleriniz dosyada veri olsa bile korunur. Yeni eklenen firmalarda dosyadaki tüm bilgiler yazılır.
              </p>
            </div>
          )}
          {importPreview && importPreview.total > 0 && (
            <label className="flex items-start gap-2.5 rounded-md border border-border p-3 cursor-pointer hover:bg-muted/40" data-testid="import-fill-only-toggle">
              <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#16A34A]" checked={importFillOnly} onChange={(e) => setImportFillOnly(e.target.checked)} data-testid="import-fill-only-checkbox" />
              <span className="text-sm">
                <span className="font-medium">Yalnızca eksik alanları doldur:</span> Şube her zaman güncellenir; yetkili, ortak, adres, telefon ve sicil bilgileri <b>yalnızca boşsa</b> yazılır.
                <span className="block text-muted-foreground mt-1">
                  {importFillOnly
                    ? "2026 tahmini, yetki belgesi, durum, sorumlu ve notlarınıza DOKUNULMAZ."
                    : "İşaretlemezseniz dosyadaki eğilim, yetki belgesi, durum ve notlar da sisteme yazılır ve mevcut değerleri değiştirir."}
                </span>
              </span>
            </label>
          )}
          {importPreview && importPreview.total > 0 && (
            <label className="flex items-start gap-2.5 rounded-md border border-border p-3 cursor-pointer hover:bg-muted/40" data-testid="import-replace-toggle">
              <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#991B1B]" checked={importReplace} onChange={(e) => setImportReplace(e.target.checked)} data-testid="import-replace-checkbox" />
              <span className="text-sm">
                <span className="font-medium">Tam yükleme:</span> Bu komitedeki mevcut <b>{total}</b> firmayı önce sil, sonra dosyadakileri yükle.
                {importReplace && <span className="block text-[#991B1B] dark:text-red-300 font-medium mt-1">⚠ Bu komitedeki mevcut {total} firma kalıcı olarak silinip dosyadaki {importPreview.total} firmayla değiştirilecek.</span>}
                {!importReplace && <span className="block text-muted-foreground mt-1">İşaretlemezseniz mevcut firmalar korunur, sadece eşleşenler güncellenir/yenileri eklenir.</span>}
              </span>
            </label>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              disabled={!importPreview || importPreview.total === 0 || importing}
              className={importReplace ? "bg-[#991B1B] hover:bg-[#991B1B]/90" : "bg-[#0F2C59] hover:bg-[#0F2C59]/90"}
              data-testid="confirm-import"
              onClick={confirmImport}>{importReplace ? "Sil ve İçe Aktar" : "İçe Aktar"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
